import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { trpc } from '@org/clients/school';
import { Badge, Button, ConfirmDialog } from '@org/ui';
import { baseCtaOf } from '../../lib/access';
import { useAccess } from '../access/use-access';
import { CheckoutSheet } from './checkout-sheet';
import { formatPrice } from './helpers';
import { P, btnBlack, btnRed, dmSerif, eyebrowStyle, fineStyle, oswald, useWidth } from './theme';

/**
 * The base access — the default learning path, paid monthly — sits at the top
 * of the store as the main thing. The button follows the student's state:
 * start the free trial, subscribe (during / after the trial), or, for group /
 * private / b2b students, "included in your plan". Paying students get a
 * quiet "cancel subscription" link.
 */
export function BaseHero() {
  const { t, i18n } = useTranslation('p2e');
  const navigate = useNavigate();
  const w = useWidth();
  const isMobile = w < 640;
  const dateLocale = i18n.language?.startsWith('pt') ? ptBR : enUS;
  const utils = trpc.useUtils();
  const { data } = useAccess();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const base = data?.base;
  const product = base?.product;
  const cta = base ? baseCtaOf(base) : null;
  const isPaidActive = cta?.kind === 'active' && base?.source === 'asaas';

  const { data: purchases } = trpc.billing.myPurchases.useQuery(undefined, { enabled: !!isPaidActive });

  const startTrial = trpc.access.startTrial.useMutation({
    onSuccess: (next) => {
      utils.access.me.setData(undefined, next);
      void utils.products.listActive.invalidate();
      void utils.progress.invalidate();
      toast.success(t('products.base.trialStarted', { count: product?.trialDays ?? 0 }));
      void navigate({ to: '/journey', search: { journeyId: undefined, celebrate: undefined, ceremony: undefined } });
    },
    onError: () => toast.error(t('products.base.trialError')),
  });

  const cancel = trpc.billing.cancelSubscription.useMutation({
    onSuccess: () => {
      void utils.access.me.invalidate();
      void utils.billing.myPurchases.invalidate();
      toast.success(t('products.base.cancelDone'));
    },
    onError: () => toast.error(t('products.base.cancelError')),
  });

  if (!product || !base || !cta) return null;

  const priceLabel =
    product.priceCents === 0 ? t('products.free') : formatPrice(product.priceCents, product.currency, i18n.language ?? 'pt');
  const endsAtLabel = base.endsAt ? format(new Date(base.endsAt), 'PP', { locale: dateLocale }) : null;

  // Newest granted base purchase paid through ASAAS = the subscription to cancel.
  const subscription = (purchases ?? []).find(
    (p) => p.source === 'asaas' && p.status === 'granted' && !!p.asaasSubscriptionId,
  );

  let button: React.ReactNode;
  let note: string;
  switch (cta.kind) {
    case 'included':
      button = (
        <Badge className="bg-[#E4F2E9] text-[#1E7A46] hover:bg-[#E4F2E9]">{t('products.base.included')}</Badge>
      );
      note = t('products.base.includedNote');
      break;
    case 'trial':
      button = (
        <Button
          type="button"
          variant="red"
          className="products-btn"
          style={btnRed}
          isLoading={startTrial.isPending}
          loadingText={t('products.base.starting')}
          onClick={() => startTrial.mutate()}
          data-testid="base-start-trial"
        >
          {t('products.base.startTrial', { count: product.trialDays ?? 0 })}
        </Button>
      );
      note = t('products.base.trialNote', { price: priceLabel });
      break;
    case 'subscribe-trial':
      button = (
        <Button type="button" variant="red" className="products-btn" style={btnRed} onClick={() => setCheckoutOpen(true)} data-testid="base-subscribe">
          {t('products.base.subscribe')}
        </Button>
      );
      note = t('products.base.trialDaysLeft', { count: cta.daysLeft ?? 0 });
      break;
    case 'active':
      button = (
        <Button type="button" variant="default" className="products-btn" style={btnBlack} onClick={() => navigate({ to: '/journey', search: { journeyId: undefined, celebrate: undefined, ceremony: undefined } })}>
          {t('products.cta.continue')}
        </Button>
      );
      note = endsAtLabel ? t('products.base.activeUntil', { date: endsAtLabel }) : t('products.base.activeNoLimit');
      break;
    default:
      button = (
        <Button type="button" variant="red" className="products-btn" style={btnRed} onClick={() => setCheckoutOpen(true)} data-testid="base-subscribe">
          {t('products.base.subscribe')}
        </Button>
      );
      note = base.state === 'expired' ? t('products.base.expiredNote') : t('products.base.subscribeNote');
  }

  return (
    <section
      aria-label={product.name}
      data-testid="base-hero"
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 220px',
        gap: isMobile ? 16 : 24,
        alignItems: 'center',
        background: P.black,
        color: P.white,
        borderRadius: 10,
        padding: isMobile ? '22px 18px' : '28px 30px',
        margin: '18px 0 26px',
        boxShadow: `8px 12px 28px ${P.shadowDeep}`,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ ...eyebrowStyle, color: P.charm }}>{t('products.base.eyebrow')}</div>
        <h2
          style={{
            fontFamily: dmSerif,
            fontWeight: 400,
            fontSize: isMobile ? 28 : 36,
            lineHeight: 1.05,
            margin: '4px 0 10px',
            textWrap: 'balance',
          } as React.CSSProperties}
        >
          {product.tagline || product.name}
        </h2>
        {product.description && (
          <p style={{ margin: '0 0 14px', fontSize: 15, lineHeight: 1.55, color: '#F8D7DD', maxWidth: '56ch' }}>{product.description}</p>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
          <div style={{ fontFamily: oswald }}>
            <b style={{ fontSize: 26, fontWeight: 700 }}>{priceLabel}</b>
            <span style={{ fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#F8D7DD', marginLeft: 6 }}>
              {t('products.base.perMonth')}
            </span>
          </div>
          {button}
        </div>
        <p style={{ ...fineStyle, color: '#F8D7DD', margin: '10px 0 0' }}>{note}</p>
        {isPaidActive && (
          <Button
            type="button"
            variant="link"
            className="mt-1 h-auto px-0 text-xs text-[#F8D7DD] underline-offset-2"
            onClick={() => setCancelOpen(true)}
            disabled={!subscription || cancel.isPending}
          >
            {t('products.base.cancel')}
          </Button>
        )}
      </div>

      <div
        aria-hidden
        style={{
          display: isMobile ? 'none' : 'block',
          aspectRatio: '3 / 4',
          borderRadius: 6,
          overflow: 'hidden',
          background: P.maxRed,
          border: `4px solid ${P.white}`,
          transform: 'rotate(2deg)',
        }}
      >
        {product.coverImageUrl ? (
          <img src={product.coverImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ height: '100%', display: 'grid', placeItems: 'center', fontFamily: oswald, fontWeight: 700, fontSize: 22, textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'center', padding: 12 }}>
            {product.name}
          </div>
        )}
      </div>

      <CheckoutSheet
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        product={{
          productId: product.productId,
          name: product.name,
          priceCents: product.priceCents,
          currency: product.currency,
          billing: 'monthly',
          coverImageUrl: product.coverImageUrl,
        }}
      />
      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={t('products.base.cancelTitle')}
        description={endsAtLabel ? t('products.base.cancelBody', { date: endsAtLabel }) : t('products.base.cancelBodyNoDate')}
        confirmLabel={t('products.base.cancelConfirm')}
        cancelLabel={t('products.base.cancelKeep')}
        destructive
        onConfirm={() => subscription && cancel.mutate({ purchaseId: subscription.purchaseId })}
      />
    </section>
  );
}
